// ============================================================
// TILT — VEC
// ------------------------------------------------------------
// The smallest possible 2D vector library. Plain {x,y} objects,
// pure functions, zero allocation discipline where it matters.
//
// WHY NOT A LIBRARY: TILT's physics must be DETERMINISTIC — the
// same inputs on two machines must produce the same ball, bit for
// bit, or PvP replay and sabotage desync. That means: fixed step,
// no Math.random in the solver, no library we don't control.
// This file is the bottom turtle.
//
// CONVENTION: +y is DOWN (screen space). Gravity is +y.
// Every table is authored in "table units" — a 400 x 700 playfield
// box. The renderer scales; the physics never sees pixels.
// ============================================================

export const v = (x = 0, y = 0) => ({ x, y });

export const add   = (a, b)    => ({ x: a.x + b.x, y: a.y + b.y });
export const sub   = (a, b)    => ({ x: a.x - b.x, y: a.y - b.y });
export const mul   = (a, s)    => ({ x: a.x * s,   y: a.y * s   });
export const neg   = (a)       => ({ x: -a.x,      y: -a.y      });
export const dot   = (a, b)    => a.x * b.x + a.y * b.y;
export const cross = (a, b)    => a.x * b.y - a.y * b.x;

export const len2 = (a) => a.x * a.x + a.y * a.y;
export const len  = (a) => Math.sqrt(a.x * a.x + a.y * a.y);

export const dist2 = (a, b) => {
  const dx = a.x - b.x, dy = a.y - b.y;
  return dx * dx + dy * dy;
};
export const dist = (a, b) => Math.sqrt(dist2(a, b));

export const norm = (a) => {
  const l = len(a);
  if (l < 1e-9) return { x: 0, y: 0 };
  return { x: a.x / l, y: a.y / l };
};

// Perpendicular (rotate +90° in screen space: right-hand becomes down)
export const perp = (a) => ({ x: -a.y, y: a.x });

// Rotate a vector by angle (radians)
export const rot = (a, ang) => {
  const c = Math.cos(ang), s = Math.sin(ang);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
};

// Rotate a point around a pivot
export const rotAround = (p, pivot, ang) =>
  add(pivot, rot(sub(p, pivot), ang));

// Reflect vector `a` about unit normal `n`, with restitution.
// r = a - (1 + e) * (a·n) * n
export const reflect = (a, n, e = 1) => {
  const d = dot(a, n) * (1 + e);
  return { x: a.x - d * n.x, y: a.y - d * n.y };
};

export const clamp = (x, lo, hi) => (x < lo ? lo : x > hi ? hi : x);

export const lerp = (a, b, t) => a + (b - a) * t;
export const lerpV = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

// Closest point on segment [a,b] to point p. The single most-used
// primitive in the whole engine — every wall, flipper, and ramp
// collision funnels through it.
export const closestOnSeg = (p, a, b) => {
  const abx = b.x - a.x, aby = b.y - a.y;
  const l2 = abx * abx + aby * aby;
  if (l2 < 1e-12) return { x: a.x, y: a.y, t: 0 };
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / l2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return { x: a.x + abx * t, y: a.y + aby * t, t };
};

// Deterministic PRNG (mulberry32). Anywhere TILT needs "randomness"
// — a bumper's kick jitter, a spinner's wobble — it pulls from a
// SEEDED stream, never Math.random. Two clients with the same seed
// see the same table. This is what makes replay/ghost/PvP possible.
export const makeRng = (seed = 1) => {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
